<template>
  <v-col
    cols="12"
    class="stay-page"
  >
    <v-card>
      <v-card-title class="align-center">
        <div class="d-flex align-center stay-title">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                :to="{ name: 'stayList' }"
                class="mr-3"
                aria-label="Retour aux séjours"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
              </v-btn>
            </template>
            <span>Retour aux séjours</span>
          </v-tooltip>
          <span>Session de séjours</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          small
          color="info"
          :loading="loading"
          @click="refresh"
        >
          <v-icon
            small
            class="mr-1"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          Actualiser
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="mb-5">
          <div class="stay-id">
            {{ $route.params.runId }}
          </div>
          <div
            v-if="session"
            class="stay-meta"
          >
            {{ formatDate(session.createdAt) }} · Moteur {{ session.engineId }} · Configuration {{ session.configurationId }}
          </div>
        </div>
        <v-alert
          v-if="refreshError"
          type="error"
          outlined
          class="stay-error"
        >
          {{ refreshError }}
        </v-alert>
        <v-progress-linear
          v-if="loading && !session"
          indeterminate
          color="info"
          aria-label="Chargement de la session"
        ></v-progress-linear>

        <template v-if="session">
          <section
            class="stay-metrics"
            aria-label="Volumes de la session"
          >
            <div
              v-for="metric in metrics"
              :key="metric.label"
              class="stay-metric"
            >
              <div class="text-caption text-uppercase">
                {{ metric.label }}
              </div>
              <div class="stay-metric-value text-h6 font-weight-bold">
                {{ formatNumber(metric.value) }}
              </div>
            </div>
          </section>
          <section class="stay-band stay-splits">
            <div>
              <div class="stay-phase-title">
                <h2>Collecte</h2><StatusBadge :status="session.downloadStatus"></StatusBadge>
              </div>
              <v-progress-linear
                :value="percentage(session.downloadFinishedCount, session.campingCount)"
                :color="statusColor(session.downloadStatus)"
                height="8"
                aria-label="Progression de la collecte"
              ></v-progress-linear>
              <div class="stay-meta">
                {{ session.downloadFinishedCount }} / {{ session.campingCount }} campings traités · {{ formatBytes(session.bytesReceived) }} reçus
              </div>
              <div class="stay-meta">
                {{ session.emptyCampingCount }} campings sans séjour
              </div>
            </div>
            <div>
              <div class="stay-phase-title">
                <h2>Import</h2><StatusBadge :status="session.importStatus"></StatusBadge>
              </div>
              <v-progress-linear
                :value="percentage(session.importFinishedCount, session.campingCount)"
                :color="statusColor(session.importStatus)"
                height="8"
                aria-label="Progression de l'import"
              ></v-progress-linear>
              <div class="stay-meta">
                {{ session.importFinishedCount }} / {{ session.campingCount }} campings traités · {{ session.successfulCampingCount }} réussis
              </div>
              <div class="stay-meta">
                <span :class="session.failedCampingCount ? 'error--text' : ''">{{ session.failedCampingCount }} en échec</span> · {{ session.retryCampingCount }} à réessayer
              </div>
            </div>
          </section>
          <v-alert
            v-if="session.filesPurgeError"
            type="error"
            outlined
            class="stay-error"
          >
            Nettoyage des fichiers : {{ session.filesPurgeError }}
          </v-alert>
          <div class="stay-footer mb-5">
            <div class="stay-meta">
              Fichiers : {{ session.filesPurgedAt ? 'supprimés le ' + formatDate(session.filesPurgedAt) : session.filesPurgeStartedAt ? 'suppression inachevée depuis le ' + formatDate(session.filesPurgeStartedAt) : 'conservés' }}
            </div>
            <div class="stay-meta">
              Dernière activité : {{ formatDate(session.lastActivityAt) }} · Heure de Paris
            </div>
          </div>
          <section class="stay-band">
            <h2>Campings de la session</h2>
            <div class="stay-filters">
              <v-text-field
                v-model="search"
                label="Camping ou identifiant SH"
                :prepend-inner-icon="icons.mdiMagnify"
                outlined
                dense
                hide-details
                clearable
                @input="campingPage = 1"
              ></v-text-field>
              <v-select
                v-model="campingStatus"
                :items="statuses"
                label="État de l'import"
                outlined
                dense
                hide-details
                clearable
                @change="campingPage = 1"
              ></v-select>
              <v-checkbox
                v-model="onlyIssues"
                label="Erreurs et reprises"
                dense
                hide-details
                class="mt-0"
                @change="campingPage = 1"
              ></v-checkbox>
            </div>
            <v-data-table
              :headers="headers"
              :items="filteredCampings"
              :loading="loading"
              :page.sync="campingPage"
              :items-per-page="25"
              :header-props="{ 'sort-by-text': 'Trier par' }"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, 100], 'items-per-page-text': 'Campings par page' }"
              class="stay-table"
              no-data-text="Aucun camping ne correspond aux filtres."
              loading-text="Actualisation du suivi…"
            >
              <template v-slot:item.campingName="{ item }">
                <router-link
                  class="stay-camping-name"
                  :to="{ name: 'campingDetails', params: { campingId: item.campingId } }"
                >
                  {{ item.campingName }}
                </router-link>
                <div class="stay-meta">
                  SH {{ item.establishmentId }}
                </div>
              </template>
              <template v-slot:item.downloadStatus="{ item }">
                <StatusBadge :status="item.downloadStatus"></StatusBadge>
                <div class="stay-meta">
                  {{ formatBytes(item.bytesReceived) }}<span v-if="item.bytesTotal !== null"> / {{ formatBytes(item.bytesTotal) }}</span>
                </div>
                <div
                  v-if="item.downloadError"
                  class="error--text text-caption"
                >
                  Erreur de collecte
                </div>
              </template>
              <template v-slot:item.processedCount="{ item }">
                <div
                  v-if="item.importStatus === 'unchanged'"
                  class="text-caption"
                >
                  Non nécessaire
                  <div class="stay-meta">
                    Données inchangées
                  </div>
                </div>
                <div
                  v-else
                  class="stay-progress"
                >
                  <div class="stay-number text-caption">
                    {{ formatNumber(item.processedCount) }} / {{ formatNumber(item.sourceCount) }}
                  </div>
                  <v-progress-linear
                    :value="percentage(item.processedCount, item.sourceCount)"
                    color="info"
                    height="4"
                    aria-label="Lignes traitées en préparation"
                  ></v-progress-linear>
                  <div
                    v-if="item.skippedCount"
                    class="stay-meta"
                  >
                    {{ formatNumber(item.skippedCount) }} ignorés
                  </div>
                </div>
              </template>
              <template v-slot:item.importStatus="{ item }">
                <StatusBadge :status="item.importStatus"></StatusBadge>
                <div
                  v-if="item.importStatus === 'publishing'"
                  class="stay-meta warning--text"
                >
                  {{ formatNumber(item.publicationRows) }} / {{ formatNumber(item.preparedCount) }} insertions non validées
                </div>
                <div
                  v-if="item.importError && item.importStatus !== 'superseded'"
                  class="error--text text-caption"
                >
                  Erreur d'import
                </div>
              </template>
              <template v-slot:item.publishedCount="{ item }">
                <span class="stay-number">{{ formatNumber(item.publishedCount) }}</span>
              </template>
              <template v-slot:item.lastActivityAt="{ item }">
                <span class="text-caption">{{ formatDate(item.lastActivityAt) }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      :aria-label="'Détail de ' + item.campingName"
                      v-bind="attrs"
                      v-on="on"
                      @click="selectedCampingId = item.id; dialog = true"
                    >
                      <v-icon>{{ icons.mdiEyeOutline }}</v-icon>
                    </v-btn>
                  </template>
                  <span>Détail du traitement</span>
                </v-tooltip>
              </template>
            </v-data-table>
            <div class="stay-meta mt-3">
              {{ filteredCampings.length }} / {{ campings.length }} campings · Suivi au {{ formatDate(lastUpdated) }}
            </div>
          </section>
        </template>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="dialog"
      max-width="820"
      scrollable
    >
      <v-card v-if="selectedCamping">
        <v-card-title class="flex-nowrap">
          <span
            class="text-subtitle-1"
            style="overflow-wrap: anywhere; min-width: 0;"
          >{{ selectedCamping.campingName }} · SH {{ selectedCamping.establishmentId }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Fermer le détail"
            @click="dialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="stay-page pt-0">
          <div class="stay-controls mb-5">
            <StatusBadge :status="selectedCamping.downloadStatus"></StatusBadge><StatusBadge :status="selectedCamping.importStatus"></StatusBadge>
          </div>
          <v-alert
            v-if="selectedCamping.downloadError"
            type="error"
            outlined
            class="stay-error"
          >
            Collecte : {{ selectedCamping.downloadError }}
          </v-alert>
          <v-alert
            v-if="selectedCamping.importError"
            :type="selectedCamping.importStatus === 'superseded' ? 'info' : 'error'"
            outlined
            class="stay-error"
          >
            Import : {{ selectedCamping.importError }}
          </v-alert>
          <dl class="stay-detail-grid">
            <div
              v-for="item in campingMetrics"
              :key="item.label"
            >
              <dt>{{ item.label }}</dt><dd>{{ item.value }}</dd>
            </div>
          </dl>
          <div
            v-if="selectedCamping.importStatus === 'publishing'"
            class="mt-5 warning--text"
          >
            Transaction en cours : {{ formatNumber(selectedCamping.publicationRows) }} / {{ formatNumber(selectedCamping.preparedCount) }} insertions non validées.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { mdiArrowLeft, mdiRefresh, mdiMagnify, mdiEyeOutline, mdiClose } from '@mdi/js'
import monitoring, { statusLabel } from './monitoring'
import StatusBadge from './StatusBadge.vue'

export default {
  components: { StatusBadge },
  mixins: [monitoring],
  data: () => ({
    session: null, campings: [], search: '', campingStatus: null, onlyIssues: false, campingPage: 1, dialog: false, selectedCampingId: null,
    icons: { mdiArrowLeft, mdiRefresh, mdiMagnify, mdiEyeOutline, mdiClose },
    statuses: ['pending', 'preparing', 'prepared', 'publishing', 'published', 'empty', 'unchanged', 'superseded', 'retry_wait', 'failed'].map(value => ({ text: statusLabel(value), value })),
    headers: [{ text: 'Camping', value: 'campingName' }, { text: 'Collecte', value: 'downloadStatus' },
      { text: 'Lignes traitées en préparation', value: 'processedCount', class: 'text-wrap', width: 180 }, { text: 'Import', value: 'importStatus' },
      { text: 'Séjours validés', value: 'publishedCount', align: 'end' }, { text: 'Activité', value: 'lastActivityAt' },
      { text: '', value: 'actions', sortable: false, width: 48 }]
  }),
  computed: {
    metrics () {
      return [{ label: 'Lignes source', value: this.session.sourceCount }, { label: 'Séjours préparés', value: this.session.preparedCount },
        { label: 'Séjours validés', value: this.session.publishedCount }, { label: 'Lignes sans hébergement ignorées', value: this.session.skippedCount }]
    },
    filteredCampings () {
      const term = (this.search || '').toLocaleLowerCase('fr').trim()

      return this.campings.filter(row => (!term || (row.campingName + ' ' + row.establishmentId).toLocaleLowerCase('fr').includes(term))
        && (!this.campingStatus || row.importStatus === this.campingStatus)
        && (!this.onlyIssues || ['failed', 'retry_wait'].includes(row.downloadStatus) || ['failed', 'retry_wait'].includes(row.importStatus)))
    },
    selectedCamping () {
      return this.campings.find(row => row.id === this.selectedCampingId) || null
    },
    campingMetrics () {
      const row = this.selectedCamping
      if (!row) return []
      const generations = row.generationDates || {}

      return [
        { label: 'Périodes source', value: this.formatNumber(row.periodCount) },
        { label: 'Lignes source', value: this.formatNumber(row.sourceCount) },
        { label: 'Lignes traitées en préparation', value: row.importStatus === 'unchanged'
          ? 'Non nécessaire (données inchangées)' : this.formatNumber(row.processedCount) + ' / ' + this.formatNumber(row.sourceCount) },
        { label: 'Séjours préparés', value: this.formatNumber(row.preparedCount) },
        { label: 'Lignes sans hébergement ignorées', value: this.formatNumber(row.skippedCount) },
        { label: 'Tentatives de collecte', value: this.formatNumber(row.downloadAttempts) },
        { label: 'Tentatives de préparation / publication', value: row.prepareAttempts + ' / ' + row.publishAttempts },
        { label: 'Prochain essai de collecte', value: this.formatDate(row.downloadNextRetryAt) },
        { label: "Prochain essai d'import", value: this.formatDate(row.importNextRetryAt) },
        { label: 'Début de collecte', value: this.formatDate(row.downloadStartedAt) },
        { label: 'Fin de collecte', value: this.formatDate(row.downloadFinishedAt) },
        { label: 'Dernière publication validée', value: this.formatDate(row.publishedAt) },
        { label: 'Séjours de cette publication', value: this.formatNumber(row.publishedCount) },
        { label: 'Génération source la plus ancienne', value: this.formatDate(generations.min) },
        { label: 'Génération source la plus récente', value: this.formatDate(generations.max) }
      ]
    }
  },
  watch: {
    '$route.params.runId' () {
      this.session = null
      this.campings = []
      this.dialog = false
      this.campingPage = 1
      this.refresh()
    }
  },
  methods: {
    async loadData (token) {
      return this.getMonitoring('/sessions/' + encodeURIComponent(this.$route.params.runId), token)
    }
  }
}
</script>

<style lang="scss" src="./stay.scss"></style>
